import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import SearchInput from '../../../components/SearchInput/SearchInput';
import {
  useNotificationRecipients,
  useSetNotificationRecipient,
} from '../../../features/notification-schedules/api';

// The admin opt-in table: one row per customer with a checkbox controlling whether
// they receive the weekly order summary. Checked = receives; unchecked = skipped.
export default function WeeklyReminderRecipients() {
  const { data: recipients, isLoading } = useNotificationRecipients();
  const setRecipient = useSetNotificationRecipient();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = recipients ?? [];
    if (!q) return list;
    return list.filter((r) =>
      [r.name, r.email].filter(Boolean).some((v) => v!.toLowerCase().includes(q))
    );
  }, [recipients, search]);

  const receivingCount = (recipients ?? []).filter((r) => r.receivesWeeklyOrderSummary).length;

  const onToggle = (customerId: string, receivesWeeklyOrderSummary: boolean) => {
    setRecipient.mutate(
      { customerId, receivesWeeklyOrderSummary },
      {
        onError: (err: any) =>
          toast.error(err?.response?.data?.error || 'Failed to update recipient.'),
      }
    );
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h3 style={{ marginTop: 0 }}>Reminder recipients</h3>
        <span className="muted tiny">
          {receivingCount} of {recipients?.length ?? 0} receiving
        </span>
      </div>
      <p className="muted tiny" style={{ marginTop: -6 }}>
        Only checked customers receive the weekly reminder. Unchecked customers get nothing.
      </p>

      {isLoading ? (
        <div className="skeleton h-40 w-full" />
      ) : (recipients?.length ?? 0) === 0 ? (
        <div className="muted tiny">No active customers.</div>
      ) : (
        <>
          <div style={{ marginBottom: 8 }}>
            <SearchInput
              className="search-input"
              placeholder="Search customers…"
              value={search}
              onValueChange={setSearch}
              aria-label="Search customers"
            />
          </div>

          <table className="table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Email</th>
                <th className="ta-center">Receives reminder</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td>{r.name || <span className="muted">—</span>}</td>
                  <td>{r.email}</td>
                  <td className="ta-center">
                    <input
                      type="checkbox"
                      checked={r.receivesWeeklyOrderSummary}
                      onChange={(e) => onToggle(r.id, e.target.checked)}
                      aria-label={`Weekly reminder for ${r.name || r.email}`}
                    />
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={3} className="muted tiny">
                    No customers match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
